import { useMemo } from 'react'
import { byId } from '../data/catalog'
import { fishStage } from '../domain/economy'
import { useAppStore } from '../store'
import { Icon } from '../components/Icons'

function formatDate(timestamp?: number) {
  if (!timestamp) return '—'
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(timestamp)
}

export function ProgressView() {
  const { state, syncTaskLogs } = useAppStore()
  const completed = state.sessions.filter((session) => session.status === 'completed')
  const totalMinutes = completed.reduce((sum, session) => sum + session.plannedMinutes, 0)
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
  const thisWeek = completed.filter((session) => (session.completedAt ?? 0) >= weekAgo)
  const focusDays = new Set(thisWeek.map((session) => new Date(session.completedAt!).toDateString())).size
  const adultFish = state.fish.filter((fish) => fishStage(fish.feedTotal) === 3).length
  const favoriteTask = useMemo(() => {
    const totals = new Map<string, number>()
    completed.forEach((session) => totals.set(session.title, (totals.get(session.title) ?? 0) + session.plannedMinutes))
    return [...totals.entries()].sort((a, b) => b[1] - a[1])[0]
  }, [completed])

  return (
    <main className="view progress-view">
      <header className="view-header"><div><p className="eyebrow">Reflection, not judgment</p><h1>Your progress</h1><p>Every completed session remains part of your cove’s story.</p></div></header>
      <section className="metric-grid">
        <article><Icon name="focus" /><strong>{totalMinutes}</strong><span>focused minutes</span></article>
        <article><Icon name="check" /><strong>{completed.length}</strong><span>sessions completed</span></article>
        <article><Icon name="cove" /><strong>{focusDays}</strong><span>focus days this week</span></article>
        <article><Icon name="sparkle" /><strong>{adultFish}</strong><span>adult fish</span></article>
      </section>
      <section className="reflection panel">
        <p className="eyebrow">This week</p>
        <h2>{thisWeek.length ? `You protected ${thisWeek.reduce((sum, session) => sum + session.plannedMinutes, 0)} minutes for what mattered.` : 'Your next calm session can begin whenever you are ready.'}</h2>
        {favoriteTask && <p>Most of your focused time has gone toward <strong>{favoriteTask[0]}</strong>. That is visible progress, not a grade.</p>}
      </section>
      <section className="history-section">
        <div className="section-heading"><div><p className="eyebrow">Session history</p><h2>Recent focus</h2></div>{state.pendingTaskLogs.length > 0 && <button className="secondary" onClick={() => void syncTaskLogs()}>Retry {state.pendingTaskLogs.length} task update{state.pendingTaskLogs.length === 1 ? '' : 's'}</button>}</div>
        <div className="history-list">
          {state.sessions.length === 0 && <div className="empty-state"><Icon name="focus" /><h3>Your first session will appear here.</h3><p>No streak pressure. Just a useful record of time you chose to protect.</p></div>}
          {state.sessions.slice(0, 20).map((session) => (
            <article key={session.id}>
              <span className={`history-status status-${session.status}`}>{session.status === 'completed' ? <Icon name="check" /> : '—'}</span>
              <div><strong>{session.title}</strong><span>{formatDate(session.completedAt ?? session.startedAt)}</span></div>
              <div className="history-meta"><b>{session.status === 'completed' ? session.plannedMinutes : Math.floor(session.activeSeconds / 60)} min</b>{session.googleTask && <span className={`sync-tag ${session.taskLogState ?? 'pending'}`}>{session.taskLogState === 'synced' ? 'Task synced' : 'Task pending'}</span>}</div>
            </article>
          ))}
        </div>
      </section>
    </main>
  )
}
